import {Component, Inject, OnDestroy, Renderer2} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {DraggingService} from '@application/services/dragging/dragging.service';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingConfiguration} from '@domain/production-schedule/drawing-configuration';
import {DrawingConfigurationWithColorAndYarnSet} from '@domain/production-schedule/drawing-configuration-with-color-and-yarn-set';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {TargetForListOfDrawingsEnum} from '@domain/target-for-list-of-drawings.enum';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {DesignPreviewComponent} from '@presentation/components/grid/design-preview/design-preview.component';
import {DrawingLibraryHelper} from '@presentation/pages/texedit/drawing-library/drawing-library-new-listview/drawing-library-helper';
import {GridCreelPreviewOfCreelPositionComponent} from '@presentation/pages/textile-data/creel/add/grid-creel-preview-of-creel-position/grid-creel-preview-of-creel-position.component';
import {PreviewType} from '@presentation/pages/textile-data/creel/add/preview-type';
import {
  AssertionUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  convertPixelsToHeightInMeter,
  convertToCommercialUnitCentimeter,
  DialogBuilderFactoryService,
  DialogType,
  EmptyCellRendererComponent,
  GridModel,
  GridOptionsBuilderFactoryService,
  NoDataOverlayComponentParams,
  NumberCellEditorComponent,
  NumberCellEditorParams,
  ProgressCellRendererComponent,
  StringUtils,
  TimeUtils,
  TranslateService,
  Unit,
  UnitValue
} from '@vdw/angular-component-library';
import {ICellRendererAngularComp} from 'ag-grid-angular';
import {
  AdvancedFilterModel,
  CellClickedEvent,
  CellEditingStoppedEvent,
  CellRendererSelectorResult,
  CellValueChangedEvent,
  ColDef,
  Column,
  ColumnVisibleEvent,
  EditableCallbackParams,
  FilterModel,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowDoubleClickedEvent,
  RowNode,
  RowSelectedEvent,
  SortModelItem,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams
} from 'ag-grid-community';
import {map, Observable, ReplaySubject, takeUntil} from 'rxjs';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '../../../../plan/production-schedule-plan';
import {GroupedDrawing} from '../grouped-drawing';
import {GroupedDrawingsSelectionService} from '../grouped-drawings-selection.service';

@Component({
  templateUrl: './drawings-grouped-detail-cell-renderer.component.html',
  styleUrls: ['./drawings-grouped-detail-cell-renderer.component.scss']
})
export class DrawingsGroupedDetailCellRendererComponent extends BaseComponent implements ICellRendererAngularComp, OnDestroy {
  public detailOptions: GridOptions;
  public loadingForTheFirstTime = true;

  private designPreviewFiltered: boolean;
  private addingItemToPath = false;
  private initialIds: string[];
  private parentState: boolean;
  private isPreSelection: boolean;
  private masterGridApi: GridApi;
  private masterNode: IRowNode;
  private detailApi: GridApi;
  private detailGridModel: GridModel;
  private productionScheduleItemMultiplier: number;
  private masterData: GroupedDrawing;
  private data: ListDrawingConfiguration[];
  private selectedPath: ProductionSchedulePath;
  private productionSchedule: ProductionSchedule;
  private productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  private selectedRestPathColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  private hasQuantitySuggestionPermission: boolean = false;
  private availableSpaceInBuggyInPicks: number = 5000;
  private buggyLengthMaxInPicks: number = 5000;
  private rollLengthColumnId = 'rollLength';

  private mainColorsSubject = new ReplaySubject<string[]>(1);
  private borderColorsSubject = new ReplaySubject<string[]>(1);
  private designNamesSubject = new ReplaySubject<string[]>(1);
  private heightsInPxSubject = new ReplaySubject<string[]>(1);
  private heightsInCMSubject = new ReplaySubject<string[]>(1);

  private readonly SLICE_SIZE = 30;
  private readonly DETAIL_ID_PREFIX = 'detail_';
  private readonly ROW_HEIGHT_WITH_PREVIEW = 96;
  private readonly ROW_HEIGHT_WITHOUT_PREVIEW = 24;

  public constructor(
    @Inject(DRAWINGS) private drawings: Drawings,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly groupedDrawingsSelectionService: GroupedDrawingsSelectionService,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly draggingService: DraggingService,
    private readonly renderer: Renderer2,
    private readonly drawingLibraryHelper: DrawingLibraryHelper
  ) {
    super();
  }

  public agInit(params: any): void {
    if (!AssertionUtils.isNullOrUndefined(params?.initialIds)) {
      this.initialIds = params.initialIds;
    }

    this.masterData = params.data;
    this.masterGridApi = params.api;
    this.masterNode = params.node.parent;
    this.parentState = params.parentState;

    this.productionScheduleItemMultiplier = params.extraData.productionScheduleItemMultiplier;
    this.isPreSelection = params.extraData.isPreSelection;
    this.selectedPath = params.extraData.selectedPath;
    this.productionSchedule = params.extraData.productionSchedule;
    this.productionSchedulePathsOfColoredYarnSet = params.extraData.productionSchedulePathsOfColoredYarnSet;
    this.hasQuantitySuggestionPermission = params.extraData.hasQuantitySuggestionPermission;
    this.availableSpaceInBuggyInPicks = params.extraData.availableSpaceInBuggyInPicks;
    this.buggyLengthMaxInPicks = params.extraData.buggyLengthMaxInPicks;

    this.groupedDrawingsSelectionService.cellRendererParams.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((updatedParams: any) => {
      this.setDataParams(updatedParams);

      if (!this.detailApi?.isDestroyed()) {
        setTimeout(() => this.detailApi?.refreshCells());
      }
    });

    this.setupDetailGrid(params.data.drawingIds, params.data.configurationIds);
  }

  public refresh(_: ICellRendererParams<any, any>): boolean {
    return false;
  }

  public ngOnDestroy(): void {
    const detailGridId = 'detail_' + this.masterNode.id;
    this.masterGridApi.removeDetailGridInfo(detailGridId);
  }

  private getPossibleColumnsValues(columnIdentifier: string): Observable<string[]> {
    return this.drawings.getPossibleValues(columnIdentifier).pipe(takeUntil(this.unSubscribeOnViewDestroy));
  }

  private setDataParams(params: any): void {
    this.productionScheduleItemMultiplier = params.extraData.productionScheduleItemMultiplier;
    this.isPreSelection = params.extraData.isPreSelection;
    this.selectedPath = params.extraData.selectedPath;
    this.productionSchedule = params.extraData.productionSchedule;
    this.productionSchedulePathsOfColoredYarnSet = params.extraData.productionSchedulePathsOfColoredYarnSet;

    if (!this.isPreSelection) {
      this.detailApi?.forEachNode((rowNode: RowNode<ListDrawingConfiguration>) => {
        if (!AssertionUtils.isNullOrUndefined(rowNode.data)) {
          const value = this.drawingLibraryHelper.getQuantitySuggestionForPath(
            rowNode.data,
            this.productionSchedule,
            this.selectedPath,
            this.availableSpaceInBuggyInPicks,
            this.buggyLengthMaxInPicks,
            this.hasQuantitySuggestionPermission,
            this.productionScheduleItemMultiplier
          );
          rowNode.setDataValue('quantity', value);
        }
      });
    }
  }

  private setupDetailGrid(drawingIds: number[], configurationIds: number[]): void {
    const defaultDetailGridOptions = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getDetailColumnDefinitions(), this.isPreSelection ? GridIdentifier.GROUPED_DESIGNS_DETAIL_PRESELECTION : GridIdentifier.GROUPED_DESIGNS_DETAIL_BUILDER)
      .withHeaderHeight(32)
      .withFloatingFiltersHeight(32)
      .withServerSideDataSource(this.SLICE_SIZE, this.getDetailServerSideDataSource(drawingIds, configurationIds))
      .withGetRowHeight(() => (this.designPreviewFiltered ? this.ROW_HEIGHT_WITHOUT_PREVIEW : this.ROW_HEIGHT_WITH_PREVIEW))
      .withStopEditingWhenCellsLoseFocus()
      .withCompactView()
      .withPopupParent(document.querySelector('body'))
      .withOnFirstDataRendered((event: FirstDataRenderedEvent) => this.onFirstDataRendered(event))
      .withOnColumnVisible((event: ColumnVisibleEvent<any>) => this.withOnColumnVisible(event))
      .withNoRowsOverlay({
        scale: 0.2,
        titleParam: 'DESIGN_LIBRARY.DESIGN',
        hideDescription: true,
        isAnyFilterPresent: () => !AssertionUtils.isNullOrUndefined(this.detailApi) && Object.values(this.detailApi?.getFilterModel())?.length > 0
      } as NoDataOverlayComponentParams);

    if (this.isPreSelection) {
      this.detailOptions = defaultDetailGridOptions
        .withRowSelection(this.isPreSelection, false)
        .withOnRowSelected((event: RowSelectedEvent) => this.onRowSelected(event))
        .build();
    } else {
      this.detailOptions = defaultDetailGridOptions
        .withOnCellValueChanged((event: CellValueChangedEvent) => {
          if (!this.addingItemToPath) {
            this.addDrawingToProductionSchedulePath(event.colDef, event.data);
          }
        })
        .withOnRowSelected((event: RowSelectedEvent) => {
          setTimeout(() => {
            if (!this.addingItemToPath && this.hasQuantitySuggestionPermission) {
              this.drawingLibraryHelper.setQuantitySuggestionForPath(event, this.productionSchedule, this.selectedPath, this.availableSpaceInBuggyInPicks, this.buggyLengthMaxInPicks);
            }
          }, 500);
        })
        .withOnRowDoubleClicked((event: RowDoubleClickedEvent) => {
          if (!this.addingItemToPath) {
            const colDef = this.getColumnDefinitionForClickedCell();
            this.addDrawingToProductionSchedulePath(colDef, event.data);
          }
        })
        .withRowDraggingOutsideGrid(
          (params: {rowNode: RowNode; dragEvent: DragEvent}) => this.drawingLibraryHelper.onRowDrag(params, this.productionScheduleItemMultiplier, this.draggingService, this.renderer),
          undefined,
          1
        )
        .withOnCellEditingStopped((event: CellEditingStoppedEvent) => {
          if (this.hasQuantitySuggestionPermission) {
            this.drawingLibraryHelper.recalculateQuantitySuggestionWhenRollLengthIsChanged(
              event,
              this.rollLengthColumnId,
              this.productionSchedule,
              this.selectedPath,
              this.buggyLengthMaxInPicks,
              this.availableSpaceInBuggyInPicks
            );
          }
        })
        .build();
    }
  }

  private withOnColumnVisible(event: ColumnVisibleEvent<any>): void {
    this.designPreviewFiltered = event.visible === false && event.columns.find((column: any) => column.colDef.cellClass === 'preview') != null;

    event.api.forEachNode((node: IRowNode) => node.setRowHeight(this.designPreviewFiltered ? this.ROW_HEIGHT_WITHOUT_PREVIEW : this.ROW_HEIGHT_WITH_PREVIEW));
    event.api.onRowHeightChanged();

    const colDefs = event.api.getColumnDefs();

    event.api.setGridOption('columnDefs', colDefs);
    event.api.refreshCells({force: true});

    if (!this.detailApi?.isDestroyed()) {
      setTimeout(() => this.detailApi?.refreshCells());
    }
  }

  private addDrawingToProductionSchedulePath(colDef: ColDef, data: ListDrawingConfiguration): void {
    if (colDef.headerName !== this.translate.instant('GENERAL.QUANTITY') && colDef.colId !== this.rollLengthColumnId) {
      this.addingItemToPath = true;

      const listDrawing = data;
      listDrawing.amount = Math.floor(listDrawing.amount / this.productionScheduleItemMultiplier);

      if (
        !AssertionUtils.isNullOrUndefined(this.selectedPath) &&
        this.productionSchedule.canAddProductionScheduleItemToProductionSchedulePathForNewBuilder(listDrawing, this.selectedPath.technicalWidthInDents)
      ) {
        this.addDrawingToPath(listDrawing);
      } else if (
        AssertionUtils.isNullOrUndefined(this.selectedPath) &&
        this.productionSchedule.canAddProductionScheduleItemToColoredYarnSet(this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent, listDrawing)
      ) {
        this.addDrawingToNewPath(listDrawing);
      } else {
        this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
          titleText: 'PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN',
          messageText: 'PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.PATH_WIDTH_DOES_NOT_EQUAL_DESIGN_WIDTH',
          type: DialogType.INFORMATION
        });
      }

      setTimeout(() => {
        this.addingItemToPath = false;
      }, TimeUtils.SECOND_IN_MS);
    }
  }

  private addDrawingToNewPath(listDrawing: ListDrawingConfiguration): void {
    this.productionSchedulePlan
      .addDrawingToColoredYarnSet(listDrawing, this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((path: ProductionSchedulePath) => {
        if (!AssertionUtils.isNullOrUndefined(path)) {
          this.selectedPath = path;
          this.selectedRestPathColoredYarnSet = undefined;
          this.groupedDrawingsSelectionService.updateSelectedPath(this.selectedPath);
          this.groupedDrawingsSelectionService.updateSelectedRestPathColoredYarnSet(this.selectedRestPathColoredYarnSet);
        }
      });
  }

  private addDrawingToPath(listDrawing: ListDrawingConfiguration): void {
    const productionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(
      this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
    );

    this.productionSchedulePlan.addDrawingToProductionSchedulePath(listDrawing, this.selectedPath.pathNumber, productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet);
  }

  private getColumnDefinitionForClickedCell(): ColDef {
    const column = this.detailApi.getAllDisplayedColumns().find((col: Column) => col.getColId() === this.detailApi.getFocusedCell().column.getColId());
    return column ? column.getColDef() : null;
  }

  private onRowSelected(event: RowSelectedEvent): void {
    if (this.isPreSelection) {
      const selectedNodes = event.api.getSelectedNodes();
      const selectionState = this.groupedDrawingsSelectionService.getParentSelectionState(this.data, selectedNodes);
      const selectedNodeIds = selectedNodes.map((node: RowNode) => node.id);
      const currentlySelectedDrawings = this.data.filter((drawing: ListDrawingConfiguration) => selectedNodeIds.includes(drawing.id));

      this.groupedDrawingsSelectionService.setDetailRowSelected(this.masterNode, selectionState, currentlySelectedDrawings);
    }
  }

  private getDetailServerSideDataSource(drawingIds: number[], configurationIds: number[]): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams): void => {
        const sortModel: SortModelItem[] = params.request.sortModel;
        const startRow: number = params.request.startRow;
        const endRow: number = params.request.endRow;
        let filterModel: FilterModel | AdvancedFilterModel = params.request.filterModel;

        if (!AssertionUtils.isNullOrUndefined(drawingIds) && !AssertionUtils.isEmpty(drawingIds)) {
          const idFilter = {
            id: {
              filterModels: [null, {values: drawingIds, filterType: 'set'}],
              filterType: 'multi'
            }
          };

          filterModel = {...filterModel, ...idFilter};
        }

        if (!AssertionUtils.isNullOrUndefined(configurationIds) && !AssertionUtils.isEmpty(configurationIds) && configurationIds[0] !== 0) {
          const configurationFilter = {
            configurationId: {
              filterModels: [null, {values: configurationIds, filterType: 'set'}],
              filterType: 'multi'
            }
          };

          filterModel = {...filterModel, ...configurationFilter};
        }

        this.detailGridModel = new GridModel(startRow, endRow, sortModel, filterModel);

        this.drawings
          .getAllWithoutImageDataFromGridModel(TargetForListOfDrawingsEnum.PRODUCTION_ORDER, this.detailGridModel, true, 'group')
          .pipe(
            map((listOfDrawings: Drawing[]) => {
              return listOfDrawings.map((drawing: Drawing) => {
                const config = this.getDrawingConfiguration(drawing, this.masterData.quality.name, this.masterData.colorSet?.name, this.masterData.commercialWidthInCM);
                return new ListDrawingConfiguration(drawing, 0, this.productionScheduleItemMultiplier, this.masterData.commercialWidthInCM, config);
              });
            }),
            takeUntil(this.unSubscribeOnViewDestroy)
          )
          .subscribe({
            next: (listOfDrawings: ListDrawingConfiguration[]) => {
              this.data = listOfDrawings;

              const endRowDrawings = listOfDrawings.length < this.SLICE_SIZE ? startRow + listOfDrawings.length : undefined;

              if (startRow === 0) {
                if (listOfDrawings.length === 0) {
                  params.api.showNoRowsOverlay();
                } else {
                  params.api.hideOverlay();
                }
              }

              if (this.loadingForTheFirstTime) {
                this.mainColorsSubject.next(this.getMainColors(listOfDrawings));
                this.borderColorsSubject.next(this.getBorderColors(listOfDrawings));
                this.designNamesSubject.next(this.getDesignNames(listOfDrawings));
                this.heightsInPxSubject.next(this.getHeightsInPx(listOfDrawings));
                this.heightsInCMSubject.next(this.getHeightInCM(listOfDrawings));
              }

              this.loadingForTheFirstTime = false;
              this.detailOptions.loadingCellRendererParams = {
                loadingForTheFirstTime: this.loadingForTheFirstTime
              };

              setTimeout(() => {
                params.success({
                  rowData: listOfDrawings,
                  rowCount: endRowDrawings
                });
              });
            },
            error: () => {
              params.success({rowData: null, rowCount: 0});
              params.api.showNoRowsOverlay();
            }
          });
      }
    };
  }

  private onFirstDataRendered(event: FirstDataRenderedEvent): void {
    this.detailApi = event.api;
    if (AssertionUtils.isNullOrUndefined(this.parentState)) {
      this.initialIds?.forEach((id: string) => {
        if (!AssertionUtils.isNullOrUndefined(this.detailApi?.getRowNode(id))) {
          this.detailApi?.getRowNode(id).setSelected(true);
        }
      });
    } else {
      this.detailApi.forEachNode((detailNode: RowNode) => detailNode.setSelected(this.parentState));
    }

    const detailGridId = `${this.DETAIL_ID_PREFIX}${this.masterNode.id}`;
    const gridInfo = {id: detailGridId, api: event.api};

    this.masterGridApi.addDetailGridInfo(detailGridId, gridInfo);

    if (!this.isPreSelection) {
      this.detailApi?.forEachNode((rowNode: RowNode<ListDrawingConfiguration>) => {
        if (!AssertionUtils.isNullOrUndefined(rowNode.data)) {
          const value = this.drawingLibraryHelper.getQuantitySuggestionForPath(
            rowNode.data,
            this.productionSchedule,
            this.selectedPath,
            this.availableSpaceInBuggyInPicks,
            this.buggyLengthMaxInPicks,
            this.hasQuantitySuggestionPermission,
            this.productionScheduleItemMultiplier
          );
          rowNode.setDataValue('quantity', value);
        }
      });
    }
  }

  private getDrawingConfiguration(drawing: Drawing, quality: string, colorSetName: string, widthInCM: number): DrawingConfiguration {
    return drawing?.configurations?.find((drawingConfiguration: DrawingConfiguration) => {
      const width = convertToCommercialUnitCentimeter({unit: Unit.MILLIMETER, value: drawingConfiguration.commercialDimensionsInMm.widthInMM} as UnitValue);
      const colorSet = drawingConfiguration instanceof DrawingConfigurationWithColorAndYarnSet ? drawingConfiguration.colorSet : null;
      return drawingConfiguration.quality?.name === quality && colorSet?.name === colorSetName && width === Number(widthInCM);
    });
  }

  private getDetailColumnDefinitions(): ColDef[] {
    return [
      this.colDefBuilderFactoryService.getBuilder().withColId('id').withField('drawing.id').withNumericMultiFilter(this.getPossibleColumnsValues('id')).withHiddenInToolPanel().withHide().build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('add')
        .withHeaderName('PRODUCTION_ORDER.DESIGN_LIBRARY.ORDER_LINES.ADD_TO_PATH')
        .withCellRenderer(ProgressCellRendererComponent, {label: 'GENERAL.ACTIONS.ADD', isSaving: this.addingItemToPath})
        .withoutFilter()
        .withSuppressMovable()
        .withHiddenInToolPanel()
        .withHide(this.isPreSelection)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withSortable(false)
        .withColId('quantity')
        .withField('amount')
        .withHeaderName('GENERAL.QUANTITY')
        .withCellEditor(NumberCellEditorComponent, (): NumberCellEditorParams => ({min: this.productionScheduleItemMultiplier, isSmall: false, autoSelect: true}) as NumberCellEditorParams)
        .withValueGetter((params: ValueGetterParams<ListDrawingConfiguration>) => {
          if (params.data?.amount && params.data.amount % 2 === 0) {
            return params.data.amount;
          } else {
            return this.drawingLibraryHelper.getQuantitySuggestionForPath(
              params.data,
              this.productionSchedule,
              this.selectedPath,
              this.availableSpaceInBuggyInPicks,
              this.buggyLengthMaxInPicks,
              this.hasQuantitySuggestionPermission,
              this.productionScheduleItemMultiplier
            );
          }
        })
        .withValueSetter((params: ValueSetterParams<ListDrawingConfiguration>) => this.setQuantity(params))
        .withEditable()
        .withOnCellClicked((event: CellClickedEvent) => this.editQuantityCell(event))
        .withSuppressMovable()
        .withRightAlignment()
        .withLockVisible()
        .withMinWidth(100)
        .withoutFilter()
        .withHiddenInToolPanel()
        .withHide(this.isPreSelection)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId(this.rollLengthColumnId)
        .withField(`rollLengthInMeter`)
        .withHeaderName('PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.ROLL_LENGTH', undefined, false, Unit.METER)
        .withOnCellClicked((event: CellClickedEvent) => this.editQuantityCell(event))
        .withSortable(false)
        .withEditable((params: EditableCallbackParams<ListDrawingConfiguration>) => this.isRollLengthEditable(params))
        .withCellEditor(NumberCellEditorComponent, (): NumberCellEditorParams => ({isSmall: false, autoSelect: true}) as NumberCellEditorParams)
        .withValueGetter((params: ValueGetterParams<ListDrawingConfiguration>) => {
          if (params.data?.rollLengthInMeter && params.data?.commercialDimensionsInCm?.heightInCM / 100 < params.data.rollLengthInMeter) {
            return params.data.rollLengthInMeter;
          } else if (params.data?.drawing?.repeated) {
            const rollLengthInMeter = params.data?.commercialDimensionsInCm?.heightInCM / 100;
            if (!AssertionUtils.isNullOrUndefined(rollLengthInMeter) && !isNaN(rollLengthInMeter)) {
              params.data.rollLengthInMeter = rollLengthInMeter;
              return params.data.rollLengthInMeter;
            } else {
              return convertPixelsToHeightInMeter(
                params?.data?.drawing?.dimensionsInPixels?.heightInPx,
                this.productionSchedule?.machineQuality?.pickDensity * 1000,
                this.productionSchedule?.machineQuality?.weaveStructure?.picksPerColorLine
              );
            }
          } else {
            return null;
          }
        })
        .withMinWidth(130)
        .withoutFilter()
        .withHide(this.isPreSelection)
        .withHiddenInToolPanel(this.isPreSelection)
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('heightInCM')
        .withField(`commercialDimensionsInCm.heightInCM`)
        .withHeaderName('GENERAL.DIMENSIONS.HEIGHT', 1, false, Unit.CENTIMETER)
        .withNumericMultiFilter(this.heightsInCMSubject.pipe(takeUntil(this.unSubscribeOnViewDestroy)))
        .withRightAlignment()
        .withHiddenInToolPanel()
        .withHide(this.isPreSelection)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('name')
        .withField(`drawing.name`, true)
        .withHeaderName('DESIGN_LIBRARY.DETAILS.NAME')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.designNamesSubject.pipe(takeUntil(this.unSubscribeOnViewDestroy)))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('quality')
        .withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY')
        .withTooltipField('quality')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.cellRendererSelector())
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('colorSet', true)
        .withHeaderName('TEXTILE_DATA.COLOR_SET.COLOR_SET')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.cellRendererSelector())
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('widthInPx')
        .withField(`drawing.dimensionsInPixels.widthInPx`)
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH', 1, false, Unit.PIXEL)
        .withRightAlignment()
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('widthInCM')
        .withField(`commercialDimensionsInCm.widthInCM`)
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH', 1, false, Unit.CENTIMETER)
        .withRightAlignment()
        .withValueFormatter((params: ValueFormatterParams) => params.data.commercialDimensionsInCm?.widthInCM?.toFixed(2))
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('heightInPx')
        .withField(`drawing.dimensionsInPixels.heightInPx`)
        .withHeaderName('GENERAL.DIMENSIONS.HEIGHT', 1, false, Unit.PIXEL)
        .withNumericMultiFilter(this.heightsInPxSubject.pipe(takeUntil(this.unSubscribeOnViewDestroy)))
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('mainColor')
        .withField('mainColor.name', true)
        .withComparator(StringUtils.stringComparator)
        .withHeaderName('GENERAL.ADVANCED_SEARCH.MAIN_COLOR')
        .withTextMultiFilter(this.mainColorsSubject.pipe(takeUntil(this.unSubscribeOnViewDestroy)))
        .withCellRendererSelector(this.cellRendererSelector(GridCreelPreviewOfCreelPositionComponent, {previewType: PreviewType.COLOR, showRgbValues: true}, true))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('borderColor')
        .withField('borderColor.name', true)
        .withComparator(StringUtils.stringComparator)
        .withHeaderName('GENERAL.ADVANCED_SEARCH.BORDER_COLOR')
        .withCellRendererSelector(this.cellRendererSelector(GridCreelPreviewOfCreelPositionComponent, {previewType: PreviewType.COLOR, showRgbValues: true}, true, false))
        .withTextMultiFilter(this.borderColorsSubject.pipe(takeUntil(this.unSubscribeOnViewDestroy)))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('preview')
        .withValueGetter((params: ValueGetterParams<any>) => params.data.drawing)
        .withHeaderName('GENERAL.PREVIEW')
        .withCellRenderer(DesignPreviewComponent)
        .withCellClass('preview')
        .withSortable(false)
        .withMinWidth(128)
        .withMaxWidth(128)
        .withoutFilter()
        .build()
    ];
  }

  private isRollLengthEditable(params: EditableCallbackParams<ListDrawingConfiguration>): boolean {
    return params.data.drawing.repeated;
  }

  private editQuantityCell(event: CellClickedEvent): void {
    if (!AssertionUtils.isNullOrUndefined(this.detailApi)) {
      this.detailApi.stopEditing();
    }

    if (event?.column?.getColDef()?.editable) {
      this.detailApi?.startEditingCell({
        rowIndex: event?.rowIndex,
        colKey: event?.column?.getColId()
      });
    }
  }

  private setQuantity(params: ValueSetterParams<ListDrawingConfiguration>): boolean {
    if (this.productionScheduleItemMultiplier === 2 && params.newValue % 2 !== 0) {
      params.newValue--;
    }
    params.data.amount = Math.max(this.productionScheduleItemMultiplier, params.newValue);
    return true;
  }

  private cellRendererSelector<TData = any, TValue = any>(
    cellRenderer: any = null,
    cellRendererParams: any = null,
    cellForMainOrBorderColor: boolean = false,
    useMainColor: boolean = true
  ): (params: ICellRendererParams<TData, TValue>) => CellRendererSelectorResult {
    return (params: ICellRendererParams<TData, TValue>): CellRendererSelectorResult => {
      if (cellForMainOrBorderColor) {
        const configuration = params.data as ListDrawingConfiguration;
        cellRendererParams.value = {creelPositionForColors: useMainColor ? configuration.mainColor : configuration.borderColor};
      }
      return params.node.level === 0 ? {component: cellRenderer ?? params.colDef.cellRenderer, params: cellRendererParams} : {component: EmptyCellRendererComponent};
    };
  }

  private getBorderColors(listDrawingConfigurations: ListDrawingConfiguration[]): string[] {
    return listDrawingConfigurations.map((detailDrawingConfig: ListDrawingConfiguration) => detailDrawingConfig.borderColor?.getFirstItem()?.name ?? '');
  }

  private getMainColors(listDrawingConfigurations: ListDrawingConfiguration[]): string[] {
    return listDrawingConfigurations.map((detailDrawingConfig: ListDrawingConfiguration) => detailDrawingConfig.mainColor?.getFirstItem()?.name ?? '');
  }

  private getDesignNames(listDrawingConfigurations: ListDrawingConfiguration[]): string[] {
    return listDrawingConfigurations.map((detailDrawingConfig: ListDrawingConfiguration) => detailDrawingConfig.drawing?.name ?? '');
  }

  private getHeightsInPx(listDrawingConfigurations: ListDrawingConfiguration[]): string[] {
    return listDrawingConfigurations.map((detailDrawingConfig: ListDrawingConfiguration) => `${detailDrawingConfig.drawing.dimensionsInPixels?.heightInPx}`);
  }

  private getHeightInCM(listDrawingConfigurations: ListDrawingConfiguration[]): string[] {
    return listDrawingConfigurations.map((detailDrawingConfig: ListDrawingConfiguration) => `${detailDrawingConfig.commercialDimensionsInCm?.heightInCM}`);
  }
}
